import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import useStore from '../store/useStore'
import { signOut } from '../services/supabase'
import { getRoadmapMetadata } from '../services/supabase'

const Dashboard = () => {
  const navigate = useNavigate()
  const { user, logout, roadmaps, setRoadmaps } = useStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadRoadmaps()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadRoadmaps = async () => {
    if (!user) return
    
    setIsLoading(true)
    
    try {
      // Load from Supabase
      const { data, error } = await getRoadmapMetadata(user.id)
      
      if (!error && data) {
        setRoadmaps(data)
      }
      
      // Try to load from Google Drive if connected
      // Note: Drive loading is optional and requires user to connect Drive first
      // This is a placeholder for future enhancement
      // Users can manually connect Drive and save roadmaps there
    } catch (error) {
      console.error('Error loading roadmaps:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSignOut = async () => {
    await signOut()
    logout()
    navigate('/')
  }

  const filteredRoadmaps = roadmaps.filter(roadmap =>
    roadmap.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    roadmap.topic?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="gradient-bg relative overflow-hidden pb-0">
      {/* Floating Background Elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-gradient-to-r from-violet-400 to-cyan-400 rounded-full opacity-20"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              x: [0, Math.random() * 10 - 5, 0],
              scale: [1, 1.2, 1],
              opacity: [0.2, 0.4, 0.2],
            }}
            transition={{
              duration: 3 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Header */}
      <header className="bg-white/90 backdrop-blur-lg border-b border-gray-200/50 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <motion.div 
              className="flex items-center gap-3"
              whileHover={{ scale: 1.02 }}
            >
              <motion.div 
                className="w-12 h-12 bg-gradient-to-br from-violet-600 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg"
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
              >
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
              </motion.div>
              <h1 className="text-2xl font-display font-bold gradient-text">PathPilot</h1>
            </motion.div>

            <div className="flex items-center gap-4">
              <motion.div 
                className="flex items-center gap-3 bg-gradient-to-r from-violet-50 to-cyan-50 px-4 py-2 rounded-full"
                whileHover={{ scale: 1.05 }}
              >
                <img
                  src={user?.user_metadata?.avatar_url || `https://ui-avatars.com/api/?name=${user?.email}`}
                  alt="Profile"
                  className="w-10 h-10 rounded-full border-2 border-white shadow-md"
                />
                <div className="hidden md:block">
                  <p className="text-sm font-semibold text-gray-900">
                    {user?.user_metadata?.full_name || user?.email}
                  </p>
                  <p className="text-xs text-gray-600">{user?.email}</p>
                </div>
              </motion.div>
              
              <motion.button
                onClick={handleSignOut}
                whileHover={{ scale: 1.1, rotate: 5 }}
                whileTap={{ scale: 0.9 }}
                className="text-gray-600 hover:text-red-600 transition-colors p-2.5 rounded-xl hover:bg-red-50 border-2 border-transparent hover:border-red-200"
                title="Sign out"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </motion.button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4">
            What do you want to learn?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Create personalized roadmaps for any skill, career, or hobby
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <input
                type="text"
                placeholder="Search your roadmaps..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-6 py-4 pl-14 rounded-2xl border-2 border-gray-200 focus:border-violet-400 focus:outline-none text-lg transition-colors"
              />
              <svg
                className="w-6 h-6 text-gray-400 absolute left-5 top-1/2 -translate-y-1/2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 20px 25px -5px rgba(139, 92, 246, 0.3)" }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/onboarding')}
              className="relative overflow-hidden bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white font-semibold py-4 px-8 rounded-xl shadow-lg flex items-center gap-3 group"
            >
              {/* Shimmer effect */}
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
              
              <svg className="w-6 h-6 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="relative z-10 text-lg">Create New Roadmap</span>
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 20px 25px -5px rgba(6, 182, 212, 0.3)" }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/community')}
              className="relative overflow-hidden bg-white hover:bg-gray-50 text-gray-700 font-semibold py-4 px-8 rounded-xl shadow-lg border-2 border-gray-200 hover:border-cyan-400 flex items-center gap-3 group transition-all"
            >
              <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span className="text-lg">Browse Community</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Important Alerts */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="max-w-4xl mx-auto mb-12"
        >
          {/* Storage Info */}
          <div className="bg-gradient-to-r from-violet-50 to-purple-50 border-2 border-violet-200 rounded-2xl p-6 shadow-lg">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-violet-500 rounded-xl flex items-center justify-center shadow-md">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-bold text-violet-900 mb-2">
                  📦 Roadmap Storage
                </h4>
                <p className="text-violet-800 mb-3">
                  Your roadmaps are securely stored in your account. You can save up to 15 roadmaps.
                </p>
                <ul className="text-sm text-violet-700 space-y-1 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-violet-500 mt-1">•</span>
                    <span>Access your roadmaps from any device</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-violet-500 mt-1">•</span>
                    <span>Click &quot;Save Roadmap&quot; button to store roadmaps</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-violet-500 mt-1">•</span>
                    <span>Download as PDF or JSON anytime</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Roadmaps Grid */}
        <div className="mt-16">
          <h3 className="text-2xl font-display font-bold text-gray-900 mb-6">
            Your Roadmaps
          </h3>

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filteredRoadmaps.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">No roadmaps yet</h4>
              <p className="text-gray-600 mb-6">
                {searchQuery ? 'No roadmaps match your search.' : 'Create your first learning roadmap to get started!'}
              </p>
              {!searchQuery && (
                <button
                  onClick={() => navigate('/onboarding')}
                  className="btn-primary"
                >
                  Create Your First Roadmap
                </button>
              )}
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredRoadmaps.map((roadmap, idx) => (
                <motion.div
                  key={roadmap.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  whileHover={{ y: -8, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}
                  onClick={() => navigate(`/roadmap/${roadmap.id}`)}
                  className="relative card p-6 cursor-pointer group overflow-hidden border-2 border-transparent hover:border-violet-200 transition-all"
                >
                  {/* Gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-violet-50 to-cyan-50 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  
                  <div className="relative z-10">
                    <div className="flex items-start justify-between mb-4">
                      <motion.div 
                        className="w-14 h-14 bg-gradient-to-br from-violet-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg"
                        whileHover={{ rotate: 360, scale: 1.1 }}
                        transition={{ duration: 0.6 }}
                      >
                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </motion.div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-xs font-semibold px-3 py-1 bg-violet-100 text-violet-700 rounded-full">
                          {roadmap.duration_weeks || 0} weeks
                        </span>
                        <span className="text-xs text-gray-500">
                          📅 {new Date(roadmap.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-violet-600 transition-colors line-clamp-1">
                      {roadmap.title || 'Untitled Roadmap'}
                    </h4>
                    
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {roadmap.topic || 'No description'}
                    </p>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                        Active
                      </span>
                      <motion.div
                        className="flex items-center gap-2 text-violet-600 font-medium"
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        <span className="text-sm">View</span>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

export default Dashboard
